/**
 * Add extra slots for a day.
 *
 * Each intervenant gets their own mini-form — building, morning block,
 * afternoon block — rather than one shared building for everyone checked:
 * a real day reconstructed by hand often has different people at different
 * sites. The day itself stays a single choice at the top.
 *
 * A day may already carry planning: the new slots then join the existing
 * tour, and a manual slot on the same intervenant/day/time as an existing
 * one wins — the old free slot is greyed out, an old booked one is refused
 * server-side. Days with planning are marked in the calendar, not disabled.
 *
 * Split into a morning and an afternoon block per intervenant — mirroring
 * the two halves of a technician's working day — rather than one free-form
 * list, so a start time can never accidentally land after lunch when it was
 * meant as a morning start, or vice versa. The preview under each block
 * always shows exactly what a submit would create, computed with the same
 * `slotTimesFrom` the server uses, so the two can never disagree about what
 * a given start time and count produce.
 *
 * Unlike BookingDialog, this creates capacity rather than filling it — the
 * weekday rule and the per-intervenant overlap rule gate the submit
 * client-side purely for a fast no, since the server repeats every one of
 * them inside its transaction and is the actual guarantee. The collisions
 * with slots already planned that day are fetched (`useAdminDaySlots`) and
 * shown before submit: a free one will be greyed, a reserved one blocks the
 * add — the server still re-checks both, this is only an early warning.
 *
 * A creation can never be undone from the app (no delete route), so the
 * first submit only validates and shows a recap; the actual POST only
 * happens once the admin confirms a second time.
 *
 * `charte.css` is kept verbatim from the legacy page and has no rules for a
 * `<select>` or a checkbox group inside `.field`, so those two controls are
 * styled inline instead of adding new selectors to that file.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  AFTERNOON_WINDOW,
  formatDayFr,
  isWeekday,
  MORNING_WINDOW,
  SLOT_DURATION_MINUTES,
  slotEndTime,
  slotTimesFrom,
  slotTimeToMinutes,
  type AddSlotsBody,
  type IntervenantView,
  type SiteSummary,
} from "@soi/contracts";
import { ApiFailure } from "../lib/api.js";
import {
  useAddSlots,
  useAdminDaySlots,
  useAdminSlotDays,
  useIntervenants,
  useSites,
} from "../lib/queries.js";
import { useToast } from "./Toast.js";

const selectStyle: React.CSSProperties = {
  width: "100%",
  fontFamily: "inherit",
  fontSize: ".95rem",
  padding: "10px 12px",
  border: "1.5px solid var(--line)",
  borderRadius: 3,
  boxSizing: "border-box",
  background: "#fff",
};

/** Orange advisory — a problem the admin should see, but not a hard block. */
const warnBoxStyle: React.CSSProperties = {
  margin: "0 0 8px",
  padding: "10px 12px",
  borderRadius: 3,
  background: "#FFF4E5",
  color: "#8A5200",
  fontSize: ".84rem",
  lineHeight: 1.5,
};

/** "08:00" (the value carried in state) -> "08h00" (what the API speaks). */
const toSlotTime = (clock: string) => clock.replace(":", "h");

const minutesToClock = (m: number) =>
  `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;

/**
 * The "HH:MM" clock strings on the quarter hour between a window's bounds,
 * inclusive — the suggestions offered in the start-time combobox.
 */
function quarterOptions(window: { start: string; end: string }): string[] {
  const out: string[] = [];
  for (let m = slotTimeToMinutes(window.start); m <= slotTimeToMinutes(window.end); m += 15) {
    out.push(minutesToClock(m));
  }
  return out;
}

/**
 * A freely typed start time, coerced onto the grid: nearest quarter hour,
 * clamped to the window. A value that cannot be read at all falls back to the
 * window's start. Runs on blur so typing is not fought mid-entry.
 */
function snapToGrid(raw: string, window: { start: string; end: string }): string {
  const m = /^(\d{1,2})\s*[:hH]\s*(\d{1,2})?$/.exec(raw.trim());
  const lo = slotTimeToMinutes(window.start);
  const hi = slotTimeToMinutes(window.end);
  if (!m) return window.start.replace("h", ":");
  const total = Number(m[1]) * 60 + Number(m[2] ?? 0);
  const snapped = Math.min(hi, Math.max(lo, Math.round(total / 15) * 15));
  return minutesToClock(snapped);
}

const WEEKDAY_LABELS = ["L", "M", "M", "J", "V", "S", "D"];
const MONTH_LABELS = [
  "janvier", "février", "mars", "avril", "mai", "juin",
  "juillet", "août", "septembre", "octobre", "novembre", "décembre",
];

const isoDay = (year: number, month: number, date: number) =>
  `${year}-${String(month + 1).padStart(2, "0")}-${String(date).padStart(2, "0")}`;

const calendarNavStyle: React.CSSProperties = {
  border: 0,
  background: "none",
  cursor: "pointer",
  fontSize: ".85rem",
  color: "var(--c1)",
  padding: 4,
};

/**
 * A month grid, not a native `<input type="date">`: the browser's own date
 * picker has no way to grey out weekends or flag days that already have
 * planning, only a contiguous min/max range. Unavailable days stay in the
 * grid rather than disappear from it — removing a Tuesday would shift every
 * later day sideways and break the week alignment the grid exists for.
 *
 * Days that already carry planning are no longer disabled — a manual add can
 * now land on them — only marked, so the admin knows the new slots join an
 * existing tour rather than start an empty day.
 */
function Calendar({
  value,
  onChange,
  plannedDays,
}: {
  value: string;
  onChange: (day: string) => void;
  plannedDays: Set<string>;
}) {
  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [viewYear, setViewYear] = useState(() => Number((value || todayIso).slice(0, 4)));
  const [viewMonth, setViewMonth] = useState(() => Number((value || todayIso).slice(5, 7)) - 1);

  const daysInMonth = new Date(Date.UTC(viewYear, viewMonth + 1, 0)).getUTCDate();
  // getUTCDay(): 0 = Sunday .. 6 = Saturday; shifted here to Monday-first.
  const leadingBlanks = (new Date(Date.UTC(viewYear, viewMonth, 1)).getUTCDay() + 6) % 7;
  const cells: Array<number | null> = [
    ...Array<null>(leadingBlanks).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  const changeMonth = (delta: number) => {
    let m = viewMonth + delta;
    let y = viewYear;
    if (m < 0) {
      m = 11;
      y -= 1;
    } else if (m > 11) {
      m = 0;
      y += 1;
    }
    setViewMonth(m);
    setViewYear(y);
  };

  return (
    <div
      data-testid="as-calendar"
      style={{ border: "1.5px solid var(--line)", borderRadius: 3, padding: 12 }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 8,
        }}
      >
        <button
          type="button"
          data-testid="as-calendar-prev"
          aria-label="Mois précédent"
          onClick={() => changeMonth(-1)}
          style={calendarNavStyle}
        >
          ◀
        </button>
        <b style={{ fontSize: ".9rem", textTransform: "capitalize" }}>
          {MONTH_LABELS[viewMonth]} {viewYear}
        </b>
        <button
          type="button"
          data-testid="as-calendar-next"
          aria-label="Mois suivant"
          onClick={() => changeMonth(1)}
          style={calendarNavStyle}
        >
          ▶
        </button>
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: 2,
          fontSize: ".72rem",
          color: "var(--mute)",
          textAlign: "center",
          marginBottom: 4,
        }}
      >
        {WEEKDAY_LABELS.map((w, i) => (
          <div key={i}>{w}</div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
        {cells.map((d, i) => {
          if (d === null) return <div key={i} />;
          const iso = isoDay(viewYear, viewMonth, d);
          const weekend = !isWeekday(iso);
          const planned = plannedDays.has(iso);
          const past = iso < todayIso;
          const disabled = weekend || past;
          const selected = iso === value;
          return (
            <button
              key={i}
              type="button"
              data-testid={`as-day-${iso}`}
              data-planned={planned ? "true" : "false"}
              disabled={disabled}
              onClick={() => onChange(iso)}
              title={
                weekend
                  ? "Week-end"
                  : past
                    ? "Jour déjà passé"
                    : planned
                      ? "Ce jour a déjà des créneaux — les nouveaux s'y ajouteront"
                      : undefined
              }
              style={{
                padding: "6px 0",
                fontSize: ".82rem",
                fontFamily: "inherit",
                border: selected ? "1.5px solid var(--c1)" : "1px solid transparent",
                borderRadius: 3,
                background: selected ? "var(--c1)" : disabled ? "var(--wash)" : "#fff",
                color: selected ? "#fff" : disabled ? "var(--mute)" : "var(--ink)",
                cursor: disabled ? "not-allowed" : "pointer",
                textDecoration: planned && !selected ? "underline" : undefined,
              }}
            >
              {d}
            </button>
          );
        })}
      </div>
    </div>
  );
}

interface BlockState {
  enabled: boolean;
  start: string;
  count: number;
}

/**
 * Start-time combobox: type a time, or open the panel and pick one. Unlike a
 * native `<datalist>`, the panel always lists *every* quarter hour of the
 * window — it never filters itself down to what has been typed. The value is
 * snapped onto the grid when focus leaves the widget.
 */
function StartTimeField({
  id,
  window,
  value,
  onChange,
}: {
  id: string;
  window: { start: string; end: string };
  value: string;
  onChange: (next: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const options = useMemo(() => quarterOptions(window), [window]);

  const closeAndSnap = () => {
    setOpen(false);
    onChange(snapToGrid(value, window));
  };

  return (
    <div
      style={{ position: "relative" }}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) closeAndSnap();
      }}
    >
      <input
        type="text"
        id={id}
        inputMode="numeric"
        autoComplete="off"
        placeholder="08:00"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "Escape") setOpen(false);
          else if (e.key === "Enter") {
            e.preventDefault();
            closeAndSnap();
          }
        }}
      />
      {open && (
        <div
          role="listbox"
          style={{
            position: "absolute",
            top: "calc(100% + 2px)",
            left: 0,
            right: 0,
            maxHeight: 200,
            overflowY: "auto",
            background: "#fff",
            border: "1.5px solid var(--line)",
            borderRadius: 3,
            zIndex: 10,
            boxShadow: "0 8px 24px rgba(0,0,0,.15)",
          }}
        >
          {options.map((t) => {
            const selected = t === value;
            return (
              <button
                key={t}
                type="button"
                role="option"
                aria-selected={selected}
                // Keep focus on the input so the widget's onBlur logic stays simple.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onChange(t);
                  setOpen(false);
                }}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  padding: "8px 12px",
                  border: 0,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  fontSize: ".92rem",
                  background: selected ? "var(--c1)" : "#fff",
                  color: selected ? "#fff" : "var(--ink)",
                }}
              >
                {t}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Block({
  label,
  window,
  state,
  onChange,
  idPrefix,
}: {
  label: string;
  window: { start: string; end: string };
  state: BlockState;
  onChange: (next: BlockState) => void;
  idPrefix: string;
}) {
  // `state.start` is freely typed, so it can be half-finished ("8", "08:0")
  // between keystrokes — only build the preview from a well-formed value.
  const times =
    state.enabled && /^\d{2}:\d{2}$/.test(state.start)
      ? slotTimesFrom(toSlotTime(state.start), state.count)
      : [];
  return (
    <div className="field">
      <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700 }}>
        <input
          type="checkbox"
          style={{ width: "auto", flexShrink: 0 }}
          checked={state.enabled}
          onChange={(e) => onChange({ ...state, enabled: e.target.checked })}
        />
        {label}
      </label>
      {state.enabled && (
        <>
          <div className="field-row" style={{ marginTop: 10 }}>
            <div className="field" style={{ margin: 0 }}>
              <label htmlFor={`${idPrefix}-start`}>Heure de début</label>
              <StartTimeField
                id={`${idPrefix}-start`}
                window={window}
                value={state.start}
                onChange={(start) => onChange({ ...state, start })}
              />
            </div>
            <div className="field" style={{ margin: 0 }}>
              <label htmlFor={`${idPrefix}-count`}>
                Nombre <span className="opt">(45 min chacun)</span>
              </label>
              <input
                type="number"
                id={`${idPrefix}-count`}
                min={1}
                max={20}
                value={state.count}
                onChange={(e) => onChange({ ...state, count: Math.max(1, Number(e.target.value) || 1) })}
              />
            </div>
          </div>
          <p style={{ fontSize: ".82rem", color: "var(--mute)", margin: "8px 0 0" }}>
            → {times.join(", ")}
          </p>
        </>
      )}
    </div>
  );
}

interface EntryState {
  enabled: boolean;
  siteSlug: string;
  morning: BlockState;
  afternoon: BlockState;
}

const defaultEntry = (): EntryState => ({
  enabled: false,
  siteSlug: "",
  morning: { enabled: true, start: "08:00", count: 1 },
  afternoon: { enabled: false, start: "13:00", count: 1 },
});

/** This intervenant's own building and own morning/afternoon blocks. */
function IntervenantEntry({
  iv,
  state,
  onChange,
  sites,
}: {
  iv: IntervenantView;
  state: EntryState;
  onChange: (next: EntryState) => void;
  sites: SiteSummary[] | undefined;
}) {
  return (
    <div
      className="field"
      style={{ border: "1.5px solid var(--line)", borderRadius: 3, padding: 12 }}
    >
      <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 700 }}>
        <input
          type="checkbox"
          style={{ width: "auto", flexShrink: 0 }}
          checked={state.enabled}
          onChange={(e) => onChange({ ...state, enabled: e.target.checked })}
        />
        {iv.displayName}
      </label>
      {state.enabled && (
        <>
          <div className="field" style={{ marginTop: 10 }}>
            <label htmlFor={`as-${iv.code}-site`}>Bâtiment</label>
            <select
              id={`as-${iv.code}-site`}
              style={selectStyle}
              value={state.siteSlug}
              onChange={(e) => onChange({ ...state, siteSlug: e.target.value })}
            >
              <option value="">— Choisir —</option>
              {sites?.map((s) => (
                <option key={s.slug} value={s.slug}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>
          <Block
            label="Matin"
            window={MORNING_WINDOW}
            state={state.morning}
            onChange={(morning) => onChange({ ...state, morning })}
            idPrefix={`as-${iv.code}-morning`}
          />
          <Block
            label="Après-midi"
            window={AFTERNOON_WINDOW}
            state={state.afternoon}
            onChange={(afternoon) => onChange({ ...state, afternoon })}
            idPrefix={`as-${iv.code}-afternoon`}
          />
        </>
      )}
    </div>
  );
}

/** A quarter-hour start time that is safe to feed to `slotTimesFrom`. */
const wellFormedStart = (start: string) => /^\d{2}:\d{2}$/.test(start);

/** `state.morning`/`state.afternoon` -> the times they'd produce, and whether they overlap. */
function entryTimes(state: EntryState) {
  const morningTimes =
    state.morning.enabled && wellFormedStart(state.morning.start)
      ? slotTimesFrom(toSlotTime(state.morning.start), state.morning.count)
      : [];
  const afternoonTimes =
    state.afternoon.enabled && wellFormedStart(state.afternoon.start)
      ? slotTimesFrom(toSlotTime(state.afternoon.start), state.afternoon.count)
      : [];
  const overlaps =
    morningTimes.length > 0 &&
    afternoonTimes.length > 0 &&
    slotTimeToMinutes(slotEndTime(morningTimes[morningTimes.length - 1]!)) >
      slotTimeToMinutes(afternoonTimes[0]!);
  return { times: [...morningTimes, ...afternoonTimes], overlaps };
}

export function AddSlotsDialog({ onClose }: { onClose: () => void }) {
  const { data: sites } = useSites();
  const { data: intervenants } = useIntervenants();
  const { data: takenDays } = useAdminSlotDays(true);
  const addSlots = useAddSlots();
  const toast = useToast();

  const [day, setDay] = useState("");
  const [entries, setEntries] = useState<Record<string, EntryState>>({});
  const [status, setStatus] = useState<{ kind: "err" | "ok"; message: string } | null>(null);
  const [busy, setBusy] = useState(false);
  // A creation can never be undone from the app: there is no delete route,
  // and adding one felt like the wrong fix for what is really a "did you
  // mean to do this?" moment — so the first submit only asks for
  // confirmation, and the actual POST happens on the second one.
  const [confirming, setConfirming] = useState(false);
  // Second gate, in its own dialog: replacing an existing free slot greys it
  // for good, so the admin says yes to that specifically.
  const [greyConfirm, setGreyConfirm] = useState(false);

  // The modal itself scrolls (max-height: 90vh); on an error, bring its top —
  // where the message now sits — back into view.
  const modalRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (status?.kind === "err") modalRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [status]);

  const activeIntervenants = useMemo(
    () => (intervenants ?? []).filter((iv) => iv.isActive),
    [intervenants],
  );
  const plannedSet = useMemo(() => new Set(takenDays?.days ?? []), [takenDays]);

  const getEntry = (code: string) => entries[code] ?? defaultEntry();
  const setEntry = (code: string, next: EntryState) =>
    setEntries((prev) => ({ ...prev, [code]: next }));

  const activeEntries = activeIntervenants
    .map((iv) => ({ iv, state: getEntry(iv.code) }))
    .filter((e) => e.state.enabled);

  // Existing slots on the chosen day, cross-referenced with what the form
  // would create: a `reserved` hit blocks the add, a free one is only greyed.
  const { data: daySlots, isLoading: daySlotsLoading } = useAdminDaySlots(day);
  const collisions = useMemo(() => {
    const existing = daySlots?.slots ?? [];
    const hits: { intervenant: string; time: string; reserved: boolean; siteName: string }[] = [];
    for (const { iv, state } of activeEntries) {
      for (const time of entryTimes(state).times) {
        const hit = existing.find((e) => e.intervenantCode === iv.code && e.time === time);
        if (hit) {
          hits.push({
            intervenant: iv.displayName,
            time,
            reserved: hit.reserved,
            siteName: hit.siteName,
          });
        }
      }
    }
    return hits;
  }, [daySlots, activeEntries]);
  const blockingCollisions = collisions.filter((c) => c.reserved);
  const greyingCollisions = collisions.filter((c) => !c.reserved);

  // Travel buffer: the intervenant already has a slot in *another* building
  // whose 45-minute window comes within a quarter hour of (or overlaps) one
  // being created. Not a blocker — the admin decides whether the move is
  // feasible — but it must be visible.
  const travelWarnings = useMemo(() => {
    const existing = daySlots?.slots ?? [];
    const out: {
      intervenant: string;
      newTime: string;
      otherTime: string;
      otherSite: string;
      overlap: boolean;
    }[] = [];
    for (const { iv, state } of activeEntries) {
      for (const t of entryTimes(state).times) {
        const s1 = slotTimeToMinutes(t);
        const e1 = s1 + SLOT_DURATION_MINUTES;
        for (const ex of existing) {
          if (ex.intervenantCode !== iv.code) continue;
          if (ex.siteSlug === state.siteSlug) continue; // same building: no travel
          if (ex.time === t) continue; // exact clash — handled by the greying/blocking flow
          const s2 = slotTimeToMinutes(ex.time);
          const e2 = s2 + SLOT_DURATION_MINUTES;
          const gap = s1 >= e2 ? s1 - e2 : s2 >= e1 ? s2 - e1 : -1; // -1 = overlap
          if (gap <= 15) {
            out.push({
              intervenant: iv.displayName,
              newTime: t,
              otherTime: ex.time,
              otherSite: ex.siteName,
              overlap: gap < 0,
            });
          }
        }
      }
    }
    return out;
  }, [daySlots, activeEntries]);

  const validate = (): boolean => {
    if (!day) {
      setStatus({ kind: "err", message: "Choisissez un jour." });
      return false;
    }
    if (!isWeekday(day)) {
      setStatus({
        kind: "err",
        message: "Impossible d'ajouter des créneaux un samedi ou un dimanche.",
      });
      return false;
    }
    if (activeEntries.length === 0) {
      setStatus({ kind: "err", message: "Sélectionnez au moins un intervenant." });
      return false;
    }
    for (const { iv, state } of activeEntries) {
      if (!state.siteSlug) {
        setStatus({ kind: "err", message: `Choisissez un bâtiment pour ${iv.displayName}.` });
        return false;
      }
      if (!state.morning.enabled && !state.afternoon.enabled) {
        setStatus({
          kind: "err",
          message: `Choisissez au moins une plage (matin ou après-midi) pour ${iv.displayName}.`,
        });
        return false;
      }
      if (entryTimes(state).overlaps) {
        setStatus({
          kind: "err",
          message: `Les créneaux du matin et de l'après-midi se chevauchent pour ${iv.displayName}.`,
        });
        return false;
      }
      for (const block of [state.morning, state.afternoon]) {
        if (
          block.enabled &&
          (!wellFormedStart(block.start) ||
            slotTimeToMinutes(toSlotTime(block.start)) % 15 !== 0)
        ) {
          setStatus({
            kind: "err",
            message: `L'heure de début doit être un quart d'heure pile (00, 15, 30 ou 45) pour ${iv.displayName}.`,
          });
          return false;
        }
      }
    }
    if (blockingCollisions.length > 0) {
      const list = blockingCollisions
        .map((c) => `${c.intervenant} à ${c.time} (${c.siteName})`)
        .join(", ");
      setStatus({
        kind: "err",
        message: `Créneau déjà réservé : ${list}. L'ajout serait refusé — retirez cet horaire ou choisissez-en un autre.`,
      });
      return false;
    }
    return true;
  };

  /** The actual POST, once every confirmation gate has been passed. */
  const doCreate = async () => {
    setGreyConfirm(false);
    setBusy(true);
    setStatus(null);
    try {
      const body: AddSlotsBody = {
        day,
        entries: activeEntries.map(({ iv, state }) => ({
          intervenantCode: iv.code,
          siteSlug: state.siteSlug,
          ...(state.morning.enabled
            ? { morning: { startTime: toSlotTime(state.morning.start), slotCount: state.morning.count } }
            : {}),
          ...(state.afternoon.enabled
            ? {
                afternoon: {
                  startTime: toSlotTime(state.afternoon.start),
                  slotCount: state.afternoon.count,
                },
              }
            : {}),
        })),
      };
      const result = await addSlots.mutateAsync(body);
      const total = result.entries.reduce((n, e) => n + e.times.length, 0);
      const superseded = result.entries.reduce((n, e) => n + e.superseded.length, 0);
      toast(
        `${total} créneau(x) ajouté(s) pour ${result.entries.length} intervenant(s)` +
          (superseded > 0 ? `, ${superseded} créneau(x) existant(s) remplacé(s).` : "."),
        "ok",
      );
      onClose();
    } catch (err) {
      // Back to the editable form: a failure here (e.g. someone booked the
      // slot in the meantime) usually means something needs to change, and the
      // recap has nothing left to offer at that point.
      setConfirming(false);
      setStatus({
        kind: "err",
        message: err instanceof ApiFailure ? err.message : "L'ajout des créneaux a échoué.",
      });
      setBusy(false);
    }
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();

    // First submit: only validate and show the recap — nothing is sent yet.
    if (!confirming) {
      if (validate()) {
        setStatus(null);
        setConfirming(true);
      }
      return;
    }

    // Don't create before the "what's already there?" check has answered —
    // otherwise a greying could slip through without its confirmation dialog.
    if (daySlotsLoading) {
      setStatus({
        kind: "err",
        message: "Vérification des créneaux déjà planifiés… réessayez dans un instant.",
      });
      return;
    }

    // From the recap: replacing an existing free slot needs an explicit yes
    // in its own dialog first — it is not undoable from the app.
    if (greyingCollisions.length > 0) {
      setGreyConfirm(true);
      return;
    }
    void doCreate();
  };

  // A reservation conflict refuses the whole add: nothing is created or
  // greyed, so the greying and travel advisories would only be noise — only
  // the red "already booked" message is shown in that case.
  const blocked = blockingCollisions.length > 0;
  const hasAdvisories =
    blocked || greyingCollisions.length > 0 || travelWarnings.length > 0;
  const advisories = (
    <>
      {blocked && (
        <p
          className="modal-status err"
          role="alert"
          style={{ margin: "0 0 8px", borderRadius: 3 }}
        >
          <b>Déjà réservé — l'ajout sera refusé :</b>
          <br />
          {blockingCollisions
            .map((c) => `${c.intervenant} à ${c.time} · ${c.siteName}`)
            .join(" — ")}
        </p>
      )}
      {!blocked && greyingCollisions.length > 0 && (
        <p role="alert" style={warnBoxStyle}>
          <b>Un créneau libre sera grisé</b> (il ne sera plus réservable) :{" "}
          {greyingCollisions
            .map((c) => `${c.intervenant} à ${c.time} · ${c.siteName}`)
            .join(" — ")}
        </p>
      )}
      {!blocked && travelWarnings.length > 0 && (
        <p role="alert" style={{ ...warnBoxStyle, margin: 0 }}>
          <b>Temps de trajet à vérifier :</b>
          {travelWarnings.map((w, i) => (
            <span key={i} style={{ display: "block", marginBottom: "10px" }}>
              {w.intervenant} — nouveau créneau {w.newTime}, déjà{" "}
              {w.overlap ? "en même temps qu'" : "à "}
              {w.otherTime} à {w.otherSite}
              {w.overlap ? " (chevauchement)" : ""}.
            </span>
          ))}
          <hr />L'intervenant aura-t-il le temps de se déplacer entre les deux bâtiments ?
        </p>
      )}
    </>
  );

  return (
   <>
    <div className="ovl" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div
        ref={modalRef}
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-slots-title"
      >
        <div className="modal-head">
          <button type="button" className="modal-close" aria-label="Fermer" onClick={onClose}>
            ✕
          </button>
          <h2 id="add-slots-title">Ajouter des créneaux</h2>
          <p>
            Pour un jour ouvré. Un créneau ajouté ici est prioritaire : un créneau
            libre au même horaire est grisé, un créneau déjà réservé bloque l'ajout.
          </p>
        </div>
        <form onSubmit={submit}>
          {status && (
            <p
              className={`modal-status ${status.kind}`}
              role={status.kind === "err" ? "alert" : "status"}
            >
              {status.message}
            </p>
          )}
          {!confirming ? (
            <div className="modal-body">
              <div className="field">
                <label>
                  Jour <span className="req">*</span>
                </label>
                <Calendar value={day} onChange={setDay} plannedDays={plannedSet} />
              </div>

              <div className="field">
                <label>
                  Intervenants <span className="req">*</span>
                </label>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {activeIntervenants.map((iv) => (
                    <IntervenantEntry
                      key={iv.code}
                      iv={iv}
                      state={getEntry(iv.code)}
                      onChange={(next) => setEntry(iv.code, next)}
                      sites={sites}
                    />
                  ))}
                </div>
              </div>

              {hasAdvisories && <div className="field">{advisories}</div>}
            </div>
          ) : (
            <div className="modal-body" data-testid="as-confirm-recap">
              <p className="modal-status err" role="alert" style={{ margin: "0 0 16px" }}>
                Attention : une fois créés, ces créneaux ne peuvent pas être supprimés depuis
                l'application. Vérifiez que les informations ci-dessous sont correctes avant de
                continuer.
              </p>
              <div className="field">
                <label>Récapitulatif</label>
                <p style={{ margin: "0 0 8px", fontSize: ".9rem" }}>
                  <b>Jour :</b> {formatDayFr(day)}
                </p>
                <ul style={{ margin: 0, paddingLeft: 20, fontSize: ".9rem", lineHeight: 1.6 }}>
                  {activeEntries.map(({ iv, state }) => {
                    const site = sites?.find((s) => s.slug === state.siteSlug);
                    const { times } = entryTimes(state);
                    return (
                      <li key={iv.code}>
                        <b>
                          {iv.displayName} — {site?.name} :
                        </b>{" "}
                        {times.join(", ")}
                      </li>
                    );
                  })}
                </ul>
              </div>

              {hasAdvisories && <div className="field">{advisories}</div>}
            </div>
          )}
          <div className="modal-actions">
            {!confirming ? (
              <>
                <button type="button" className="btn-cancel" onClick={onClose}>
                  Annuler
                </button>
                <button
                  type="submit"
                  className="btn-submit"
                  disabled={blockingCollisions.length > 0}
                  title={
                    blockingCollisions.length > 0
                      ? "Un créneau visé est déjà réservé — l'ajout serait refusé."
                      : undefined
                  }
                >
                  Continuer
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="btn-cancel"
                  disabled={busy}
                  onClick={() => setConfirming(false)}
                >
                  Modifier
                </button>
                <button
                  type="submit"
                  className="btn-submit"
                  disabled={busy}
                  data-testid="as-confirm-create"
                >
                  {busy ? "Envoi en cours…" : "Confirmer la création"}
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>

    {greyConfirm && (
      <div
        className="ovl"
        onClick={(e) => e.target === e.currentTarget && !busy && setGreyConfirm(false)}
      >
        <div
          className="modal"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="grey-confirm-title"
          style={{ maxWidth: 420 }}
        >
          <div className="modal-head">
            <h2 id="grey-confirm-title">Remplacer un créneau existant ?</h2>
            <p>Cette action ne peut pas être annulée depuis l'application.</p>
          </div>
          <div className="modal-body">
            <p style={{ margin: "0 0 10px", fontSize: ".9rem" }}>
              {greyingCollisions.length > 1
                ? "En confirmant, ces créneaux libres seront grisés et ne seront plus réservables par les utilisateurs :"
                : "En confirmant, ce créneau libre sera grisé et ne sera plus réservable par les utilisateurs :"}
            </p>
            <ul
              style={{ margin: 0, paddingLeft: 20, fontSize: ".9rem", lineHeight: 1.6 }}
              data-testid="grey-confirm-list"
            >
              {greyingCollisions.map((c, i) => (
                <li key={i}>
                  <b>
                    {c.intervenant} à {c.time}
                  </b>{" "}
                  · {c.siteName}
                </li>
              ))}
            </ul>
          </div>
          <div className="modal-actions">
            <button
              type="button"
              className="btn-cancel"
              disabled={busy}
              onClick={() => setGreyConfirm(false)}
            >
              Annuler
            </button>
            <button
              type="button"
              className="btn-submit"
              disabled={busy}
              data-testid="grey-confirm-ok"
              onClick={() => void doCreate()}
            >
              {busy ? "Envoi en cours…" : "Confirmer et griser"}
            </button>
          </div>
        </div>
      </div>
    )}
   </>
  );
}
